import { HttpClient } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { ComponentModalBase } from '@framework/base';
import { Subscription } from 'rxjs';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { VttActions } from '@modules/company-admin/store/actions';
import { Store } from '@ngrx/store';
import { CoreState } from '@app/store/core/states';
import { Actions, ofType } from '@ngrx/effects';
import { ToastService } from '@framework/services';


@Component({
  selector: 'app-vtt-editor',
  templateUrl: './vtt-editor-modal.component.html',
  styleUrls: ['./vtt-editor-modal.component.scss'],
  animations: [
    trigger('expandBox', [
      state('collapsed', style({ height: '0px', opacity: 0, padding: '0 12px' })),
      state('expanded', style({ height: '*', opacity: 1, padding: '12px' })),
      transition('collapsed <=> expanded', animate('320ms ease-in-out'))
    ])
  ]
})
export class VttEditorComponent extends ComponentModalBase<any> {
  vttForm!: FormGroup;
  languages :any;
  vttContent = '';
  loading = false;
  languageName:any = '';
  public language_url:any;
  public track_id:any;
  public language_code:any;
  public languagename:any;
  public loadVttSuccessSubscription:Subscription;

  // UI flags
  isAdding = false;         // add-mode toggle
  showEditor = false;       // whether preview/editor area is visible
  private subs: Subscription[] = [];
  constructor(
    private formBuilder: FormBuilder,
    private http: HttpClient,
    private store$: Store<CoreState>,
    private actions$: Actions,
    public toastService: ToastService
  ) {
    super();
  }

  protected onInit(): void {
     this.store$.dispatch(new VttActions.LoadVtt(this.initialState.company_id,this.initialState?.asset_id));
    this.vttForm = this.formBuilder.group({
      language: ['en', Validators.required],
      vttUrl: [''],
      language_code:[],
      languageName:[]
    });

    this.subscribe();
    // Fetch initial language vtt
    this.fetchVTT(this.vttForm.value.language);

    // valueChanges for vttUrl - typed to string safely
    const s = this.vttForm.get('vttUrl')!.valueChanges
      .pipe(debounceTime(500))
      .subscribe(v => {
        const url = String(v || '').trim();
        // only preview when in add mode and looks like a URL
        if (this.isAdding && url && (url.startsWith('http://') || url.startsWith('https://'))) {
          // we intentionally do not automatically preview while typing to avoid accidental loads
          // but this will auto-preview after 500ms of pause — you can change behavior as needed
          this.previewVTT(url);
        }
      });
    this.subs.push(s);
  }

  protected onDestroy(): void {}
  protected onChanges(): void {}
  protected onDoCheck(): void {}
  protected onAfterContentInit(): void {}
  protected onAfterContentChecked(): void {}
  protected onAfterViewInit(): void {}
  protected onAfterViewChecked(): void {}

  public subscribe(){
         this.loadVttSuccessSubscription = this.actions$.pipe(
          ofType<VttActions.LoadVttSuccess>(VttActions.VttActionTypes.LOAD_SUCCESS)
        ).subscribe((action: any) => {
          if(action && action.response.language && action.response.language && action.response.language.length){
              this.languages= action.response.language;
              this.vttForm.controls.language.setValue(action.response.language[0].url)
              this.track_id = action.response.language[0].track_id;
              this.language_code = action.response.language[0].language_code;
              this.language_url = action.response.language[0].url;
              this.languageName = action.response.language[0].name
              this.fetchVTT(action.response.language[0].url)
           }
        })
  }
  fetchVTT(language: string) {
    this.loading = true;
    this.showEditor = true; // keep editor area visible (no jumping)
    // TODO: replace with your backend endpoint to fetch by language
    const url = language;

    this.http.get(url, { responseType: 'text' }).subscribe({
      next: (data: string) => {
        this.vttContent = data;
        this.loading = false;
        // small delay so animation looks natural
      },
      error: () => {
        this.vttContent = '';
        this.loading = false;
      }
    });
  }

  // When language dropdown changes
  onLanguageChange() {
    if (!this.isAdding) {
      const lang = this.vttForm.value.language;
      const url = this.languages.find(language => language.url === lang).url;
      this.language_url = url;
      this.track_id = this.languages.find(language => language.url === lang).track_id;
      this.language_code = this.languages.find(language => language.url === lang).language_code
      this.languageName = this.languages.find(language => language.url === lang).name
      this.fetchVTT(url);
    }
  }

  // Toggle add mode
  toggleAddMode() {
    this.isAdding = !this.isAdding;
    // this.vttForm.patchValue({ vttUrl: '' });
    // const url = this.languages.find(language => language.url === lang).url;
    // this.vttContent = '';
    this.vttForm.controls.language.setValue(this.language_url)



    if(this.isAdding){
      this.showEditor = false;
      this.loading = false;
    }
    else{
      this.showEditor = true;
      this.loading = true;
      this.onLanguageChange()
    }
  }

  // Called when user clicks Add (explicit)
  addVTT() {
    const url = String(this.vttForm.value.vttUrl || '').trim();
    const language = this.vttForm.value.language;

    if (!url || !(url.startsWith('http://') || url.startsWith('https://'))) {
      this.toastService.error('Please enter a valid URL that starts with http:// or https://');
      return;
    }

    this.updateVTT();

    // Show animation expansion and loading notification
    this.loading = true;
    this.showEditor = false;

    // small animation delay so UI expands smoothly before content loads
    setTimeout(() => {
      this.previewVTT(url, /*isAddCall*/ true, () => {
      });
    }, 240);
  }

  // Preview VTT from url (used for add and also for live preview)
  previewVTT(url: string, isAddCall = false, after?: () => void) {
    this.loading = true;
    // fetch text
    this.http.get(url, { responseType: 'text' }).subscribe({
      next: (data: string) => {
        this.vttContent = this.cleanVTT(data);
        this.loading = false;
        this.showEditor = true;     // reveal editor
        // when adding, the textarea should remain read-only while loading occurred; now it's editable
        if (after) after();
      },
      error: () => {
        this.loading = false;
        this.showEditor = false;
        // alert('Could not fetch VTT from provided URL.');
      }
    });
  }

  // Update (save) VTT content to backend
  updateVTT() {

    if(this.isAdding){
      let payload = {
        url:this.vttForm.controls.vttUrl.value,
        languageName: this.vttForm.controls.languageName.value,
        language_code: this.vttForm.controls.language_code.value,
      }

      this.store$.dispatch(new VttActions.AddVtt(this.initialState.company_id,this.initialState?.asset_id,payload));

      this.toastService.success("Added , Please check after 5 to 10 minutes");
    }
    else{
      const language = this.vttForm.value.language;
      let payload = {
        vtt:this.vttContent,
        asset_id: this.initialState.asset_id,
        track_id: this.track_id,
        language_code:this.language_code,
        languageName:this.languageName
      }

      this.store$.dispatch(new VttActions.EditVtt(this.initialState.company_id,this.initialState?.asset_id,payload));
      this.toastService.success("Added , Please check after 5 to 10 minutes");
    }
    this.close();

  }

  // Delete VTT for selected language
  deleteVTT() {
    const language = this.vttForm.value.language;
    this.store$.dispatch(new VttActions.DeleteVtt(this.initialState.company_id,this.initialState?.asset_id,this.track_id));
    this.languages = this.languages.filter(lang => lang.url !== language);
    if(this.languages.length){
      const lang = this.languages[0].url;
      this.vttForm.controls.language.setValue(this.languages[0].url)
      this.track_id = this.languages[0].track_id;
      this.language_code = this.languages[0].language_code;  
      this.fetchVTT(lang)
    }
    else{
      this.vttContent = '';
      this.showEditor = false;
      this.isAdding = true;
    }
 
    this.toastService.success("Deleted , Please check after few minutes");
  this.close();

  }

  cl
  // Utility: remove unwanted tags (<h1>, <p> etc.)
  private cleanVTT(content: string): string {
    if (!content) return '';
    // remove any <h1>, <p> tags but keep inner text
    return content
      .replace(/<\/?(h1|p)[^>]*>/gi, '')
      .replace(/\r\n/g, '\n'); // normalize windows line endings
  }

  // Close modal (tie to your base class close if needed)
  close() {
    // If ComponentModalBase has close, call it or implement proper close
   this.onClose.emit(true)
  }
}
